/**
 * POM-6537 — Bulk Lot Update's preview: what Apply would do to every row of the picked NDC.
 *
 * Built from the same plan Apply stages (bulkApplyPlan), so the preview cannot promise a row the
 * Apply would leave out. Each row of the NDC lands in one group: it changes, it changes with part
 * (or, recorded by hand, all) of its fill as System Deficit, it is already on the lot, or it is
 * skipped, with the reason. A row the scope leaves out (a fine row while only Needs attention is
 * asked for) is not mentioned, unless it is already on the lot or holds a change of its own.
 *
 * Pure: no React, no network.
 */

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { BULK_APPLY_SCOPE } from './bulkApplyPlan';
import { REVIEW_GROUP, reviewGroupFor } from './bulkLotNdcReviewGroups';
import { ROW_STATUS, toDateOnly } from './bulkLotNdcStatus';
import { statusLabelFor } from './bulkLotNdcStatusLabels';
import { isSameLotNumber } from './bulkManualLotEntry';

const soleRecordedLot = (item) =>
  (item.recordedLots ?? []).length === 1 ? item.recordedLots[0] : null;

/** "Lot4", "LotA, LotB" or "No lot": what the row records now. */
const describeCurrentLot = (item) =>
  (item.recordedLots ?? []).map((lot) => lot.lotNumber).join(', ') || 'No lot';

const isOnPickedLot = ({ item, pick }) => {
  const recorded = soleRecordedLot(item);

  return (
    !!recorded &&
    isSameLotNumber(recorded.lotNumber, pick.lotNumber) &&
    toDateOnly(recorded.expirationDate) === toDateOnly(pick.expirationDate)
  );
};

/** Whether the preview mentions a row that will not change. */
const isMentioned = ({ row, scope }) => {
  const group = reviewGroupFor(row.status);
  if (group === REVIEW_GROUP.NO_ACTION_NEEDED) return scope === BULK_APPLY_SCOPE.ALL_ROWS;

  return true;
};

const describeSkip = ({ row, pick }) => {
  const { item, status } = row;
  if (status === ROW_STATUS.ALREADY_DISPENSED) return 'Already dispensed';
  if (status === ROW_STATUS.LOCKED_FOR_COUNT) return 'Locked for physical count';
  if (reviewGroupFor(status) === REVIEW_GROUP.PENDING_COMMIT) {
    return 'Has a change of its own waiting to be committed';
  }
  if (pick.isManual) {
    return item.fulfillingPharmacyId === pick.facilityId
      ? 'Cannot take a lot recorded by hand'
      : `A lot typed by hand here applies only at ${pick.facilityName}`;
  }
  if (!pick.stockedFacilityIds.has(item.fulfillingPharmacyId)) {
    return `${pick.lotNumber} is not stocked at ${item.fulfillingPharmacyName || 'its facility'}`;
  }
  // Any row that may record System Deficit claims the rest (bulkApplyPlan), so only one that may
  // not is left short, whether it needs attention or is fine.
  return `Not enough of ${pick.lotNumber} left, and it may not record ${SYSTEM_DEFICIT}`;
};

// By Rx number, so a row is easy to find in a group (the plan's own order is the stock's order).
const compareByRxNumber = (left, right) =>
  String(left.rxNumber).localeCompare(String(right.rxNumber), undefined, { numeric: true });

/** What Apply stages: the plan's ticked entries, in the shape stageLotNdcChange takes. */
export const toBulkChanges = (entries) =>
  (entries ?? [])
    .filter((entry) => entry.isTicked)
    .map((entry) => ({
      item: entry.row.item,
      requestLineItemId: entry.row.item.requestLineItemId,
      ...entry.change,
    }));

/**
 * `ndcRows` are the review's rows of the picked NDC (buildBulkLotUpdateChoices' `ndcRows`),
 * `entries` the plan's, and `pick` what was picked: `{ lotNumber, expirationDate, isManual }`, with
 * `stockedFacilityIds` (a Set) for a stock lot, or `facilityId` and `facilityName` for a lot
 * recorded by hand.
 *
 * Returns the four groups, each `{ requestLineItemId, rxNumber, facilityName, fillQuantity,
 * currentLot, statusLabel }` plus `deficitQty` and `fromStockQty` on a change and `reason` on a
 * skip, and the totals the confirmation states.
 */
export const describeBulkLotUpdate = ({ ndcRows, entries, pick, scope }) => {
  const appliedById = new Map(
    (entries ?? [])
      .filter((entry) => entry.isTicked)
      .map((entry) => [entry.row.item.requestLineItemId, entry])
  );
  const outcome = { change: [], changeWithDeficit: [], alreadyOn: [], skipped: [] };

  (ndcRows ?? []).forEach((row) => {
    const { item } = row;
    const line = {
      requestLineItemId: item.requestLineItemId,
      rxNumber: item.rxNumber,
      facilityName: item.fulfillingPharmacyName,
      fillQuantity: Number(item.fillQuantity ?? 0),
      currentLot: describeCurrentLot(item),
      // Why a row with a lot needs attention, as its badge says it; a row with none says "No lot".
      statusLabel:
        (item.recordedLots ?? []).length > 0 &&
        reviewGroupFor(row.status) === REVIEW_GROUP.NEEDS_ATTENTION
          ? statusLabelFor(row)
          : null,
    };
    const entry = appliedById.get(item.requestLineItemId);

    if (entry) {
      const deficitQty = Number(entry.deficitQty ?? 0);
      const group = deficitQty > 0 ? outcome.changeWithDeficit : outcome.change;
      group.push({
        ...line,
        deficitQty,
        fromStockQty: Math.max(0, line.fillQuantity - deficitQty),
      });
    } else if (isOnPickedLot({ item, pick })) {
      outcome.alreadyOn.push(line);
    } else if (isMentioned({ row, scope })) {
      outcome.skipped.push({ ...line, reason: describeSkip({ row, pick }) });
    }
  });

  Object.values(outcome).forEach((group) => {
    group.sort(compareByRxNumber);
  });

  return {
    ...outcome,
    changeCount: outcome.change.length + outcome.changeWithDeficit.length,
    deficitQty: outcome.changeWithDeficit.reduce((sum, line) => sum + line.deficitQty, 0),
  };
};
